import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Plus, Check, X, AlertCircle, PackagePlus } from 'lucide-react';
import api from '../../services/api';

/**
 * ProductSearchModal — Search global product master, assign to site, or create unmatched item / product request.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   sessionId: number
 *   siteId: number
 *   context: 'count' | 'delivery' | 'credit'
 *   onProductAssigned: (result) => void  — called after assigning an existing product
 *   onUnmatchedCreated: (item) => void   — called after creating unmatched item
 */
export default function ProductSearchModal({
  isOpen,
  onClose,
  sessionId,
  siteId,
  context = 'count',
  onProductAssigned,
  onUnmatchedCreated,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(null);
  const [mode, setMode] = useState('search'); // search | unmatched | request
  // iter374 — Unmatched items now support split lines (mirrors the
  // inventory-product split UX). Each line is { unit_label, quantity }.
  // On submit, the dominant unit (most frequent / first non-blank)
  // becomes the persisted `unit_label`; quantity is the SUM of lines
  // that share that unit. Lines on other units are appended to `notes`
  // for the admin's reconciliation review.
  const [unmatchedForm, setUnmatchedForm] = useState({
    product_name: '',
    lines: [{ unit_label: '', quantity: '' }],
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (isOpen) {
      setMode('search');
      setQuery('');
      setResults([]);
      setUnmatchedForm({ product_name: '', lines: [{ unit_label: '', quantity: '' }], notes: '' });
    }
  }, [isOpen]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await api.get(`/count/products/search?q=${encodeURIComponent(q)}&site_id=${siteId}`);
      setResults(res.data.items || []);
    } catch (e) {
      console.error('Search failed:', e);
    } finally {
      setSearching(false);
    }
  }, [siteId]);

  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  const handleAssign = async (product) => {
    setAssigning(product.id);
    try {
      const res = await api.post('/count/products/assign-to-site', {
        product_id: product.id,
        site_id: siteId,
        session_id: sessionId,
      });
      if (res.data.status === 'ok') {
        onProductAssigned?.(res.data);
        onClose();
      }
    } catch (e) {
      console.error('Assign failed:', e);
    } finally {
      setAssigning(null);
    }
  };

  const handleCreateUnmatched = async () => {
    if (!unmatchedForm.product_name.trim()) return;
    setSubmitting(true);
    try {
      // iter374 — Consolidate split lines into the canonical
      // (unit_label, quantity, notes) shape the backend expects.
      // Strategy:
      //   * Drop blank lines.
      //   * Group by unit_label (case-insensitive).
      //   * Pick the unit with the largest cumulative quantity as the
      //     "primary" unit + sum of those lines as the quantity.
      //   * Any other-unit lines get appended to notes for admin
      //     reconciliation (e.g. "Split: 12 bottle, 3 case").
      const validLines = (unmatchedForm.lines || []).filter(ln =>
        ln && (ln.quantity !== '' && ln.quantity != null) &&
        !isNaN(parseFloat(ln.quantity)) && parseFloat(ln.quantity) > 0
      );
      const byUnit = new Map();
      for (const ln of validLines) {
        const key = (ln.unit_label || '').trim().toLowerCase();
        const cur = byUnit.get(key) || { unit_label: ln.unit_label || '', total: 0 };
        cur.total += parseFloat(ln.quantity);
        byUnit.set(key, cur);
      }
      let primaryUnit = null;
      let primaryQty = null;
      for (const [, v] of byUnit) {
        if (primaryQty == null || v.total > primaryQty) {
          primaryUnit = v.unit_label;
          primaryQty = v.total;
        }
      }
      let combinedNotes = (unmatchedForm.notes || '').trim();
      if (byUnit.size > 1) {
        const breakdown = [...byUnit.values()]
          .map(v => `${v.total} ${v.unit_label || '(no unit)'}`)
          .join(', ');
        combinedNotes = combinedNotes
          ? `${combinedNotes}\nSplit: ${breakdown}`
          : `Split: ${breakdown}`;
      }
      const res = await api.post(`/count/sessions/${sessionId}/unmatched-items`, {
        item_type: context,
        product_name: unmatchedForm.product_name,
        unit_label: primaryUnit || null,
        quantity: primaryQty != null ? primaryQty : null,
        notes: combinedNotes || null,
      });
      if (res.data.status === 'ok') {
        onUnmatchedCreated?.(res.data.item);
        // Also submit a product request so it goes into the review queue
        await api.post('/count/product-requests', {
          site_id: siteId,
          session_id: sessionId,
          product_name: unmatchedForm.product_name,
          unit_label: primaryUnit || null,
          notes: combinedNotes || null,
        }).catch(() => {}); // Best-effort
        onClose();
      }
    } catch (e) {
      console.error('Create unmatched failed:', e);
    } finally {
      setSubmitting(false);
    }
  };

  // iter374 — Split-line helpers (mirror inventory-product split UX).
  const updateLine = (idx, field, value) => {
    setUnmatchedForm(prev => {
      const lines = prev.lines.map((ln, i) => i === idx ? { ...ln, [field]: value } : ln);
      return { ...prev, lines };
    });
  };
  const addLine = () => {
    setUnmatchedForm(prev => ({
      ...prev,
      // Inherit the last line's unit_label as a sensible default so the
      // user can keep typing without re-picking the unit.
      lines: [...prev.lines, { unit_label: prev.lines[prev.lines.length - 1]?.unit_label || '', quantity: '' }],
    }));
  };
  const removeLine = (idx) => {
    setUnmatchedForm(prev => ({
      ...prev,
      lines: prev.lines.filter((_, i) => i !== idx),
    }));
  };

  // Running balance per unit (intelligent: groups by unit_label, sums).
  const runningBalance = (() => {
    const groups = new Map();
    for (const ln of unmatchedForm.lines || []) {
      if (!ln || ln.quantity === '' || ln.quantity == null) continue;
      const q = parseFloat(ln.quantity);
      if (isNaN(q) || q <= 0) continue;
      const key = (ln.unit_label || '').trim() || '(no unit)';
      groups.set(key, (groups.get(key) || 0) + q);
    }
    return [...groups.entries()]; // [['bottle', 12], ['can', 3]]
  })();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" data-testid="product-search-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[85vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h2 className="font-semibold text-slate-900" data-testid="product-search-title">
            {mode === 'search' ? 'Add Product' : mode === 'unmatched' ? 'Log Unmatched Item' : 'Request New Product'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400" data-testid="product-search-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search mode */}
        {mode === 'search' && (
          <>
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  ref={inputRef}
                  value={query}
                  onChange={handleQueryChange}
                  placeholder="Search product master..."
                  className="pl-9 h-11"
                  data-testid="product-search-input"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-2 min-h-[200px]">
              {searching && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              )}

              {!searching && results.length === 0 && query.length >= 2 && (
                <div className="text-center py-8 text-slate-400" data-testid="product-search-no-results">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No products found for "{query}"</p>
                  <p className="text-xs mt-1 text-slate-400">You can log it as an unmatched item below.</p>
                </div>
              )}

              {!searching && results.length > 0 && (
                <div className="space-y-1">
                  {results.map(product => (
                    <div
                      key={product.id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 transition-colors"
                      data-testid={`product-search-result-${product.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{product.product_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {product.brand && <span className="text-xs text-slate-400">{product.brand}</span>}
                          {product.category && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{product.category}</Badge>
                          )}
                          {product.assigned_to_site && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-green-50 text-green-700 border-green-200">
                              <Check className="w-3 h-3 mr-0.5" /> On site
                            </Badge>
                          )}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant={product.assigned_to_site ? 'outline' : 'default'}
                        onClick={() => handleAssign(product)}
                        disabled={assigning === product.id}
                        className="min-h-[36px] shrink-0 ml-2"
                        data-testid={`product-assign-btn-${product.id}`}
                      >
                        {assigning === product.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : product.assigned_to_site ? (
                          'Add to count'
                        ) : (
                          <>
                            <Plus className="w-4 h-4 mr-1" /> Assign & Count
                          </>
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Switch to unmatched mode */}
            <div className="px-4 py-3 border-t border-slate-200">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setMode('unmatched');
                  setUnmatchedForm(prev => ({ ...prev, product_name: query }));
                }}
                data-testid="product-search-create-unmatched-btn"
              >
                <PackagePlus className="w-4 h-4 mr-2" />
                Product not listed? Log unmatched item
              </Button>
            </div>
          </>
        )}

        {/* Unmatched item mode */}
        {mode === 'unmatched' && (
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <p className="text-xs text-slate-500 mb-4">
              This item will be flagged for admin review. You can continue counting.
            </p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Product Name *</label>
                <Input
                  value={unmatchedForm.product_name}
                  onChange={(e) => setUnmatchedForm(prev => ({ ...prev, product_name: e.target.value }))}
                  placeholder="Product name"
                  className="h-11"
                  data-testid="unmatched-product-name"
                />
              </div>
              {/* iter374 — multi-line split entry, mirrors the inventory
                  product split UX (per-line quantity + measure). */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-slate-600">Quantity lines</label>
                  <button
                    type="button"
                    onClick={addLine}
                    className="text-xs text-indigo-600 hover:text-indigo-800 inline-flex items-center gap-1"
                    data-testid="unmatched-add-line"
                  >
                    <Plus className="w-3 h-3" /> Add line
                  </button>
                </div>
                <div className="space-y-1.5">
                  {(unmatchedForm.lines || []).map((ln, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_28px] gap-2 items-center" data-testid={`unmatched-line-${i}`}>
                      <Input
                        value={ln.unit_label}
                        onChange={(e) => updateLine(i, 'unit_label', e.target.value)}
                        placeholder="Unit (e.g. bottle)"
                        className="h-10 text-sm"
                        data-testid={`unmatched-line-unit-${i}`}
                      />
                      <Input
                        type="number"
                        inputMode="decimal"
                        pattern="[0-9]*\.?[0-9]*"
                        value={ln.quantity}
                        onChange={(e) => updateLine(i, 'quantity', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        placeholder="0"
                        className="h-10 text-sm"
                        data-testid={`unmatched-line-qty-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeLine(i)}
                        disabled={(unmatchedForm.lines || []).length <= 1}
                        className="text-slate-400 hover:text-rose-500 disabled:opacity-30 disabled:cursor-not-allowed"
                        data-testid={`unmatched-line-remove-${i}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Running balance (intelligent fallback: groups by
                    unit_label since unmatched items have no
                    conversion table). */}
                {runningBalance.length > 0 && (
                  <div
                    className="mt-2 px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded text-[11px] text-slate-700"
                    data-testid="unmatched-running-balance"
                  >
                    <span className="font-medium text-slate-600">Total: </span>
                    {runningBalance.map(([u, t], i) => (
                      <span key={u} className="tabular-nums">
                        {i > 0 && <span className="text-slate-400 mx-1">·</span>}
                        {t} {u}
                      </span>
                    ))}
                    {runningBalance.length > 1 && (
                      <span className="block text-[10px] text-slate-400 italic mt-0.5">
                        Multiple units — admin will reconcile during review.
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Notes (optional)</label>
                <textarea
                  value={unmatchedForm.notes}
                  onChange={(e) => setUnmatchedForm(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any additional details..."
                  rows={2}
                  className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:ring-1 focus:ring-slate-300"
                  data-testid="unmatched-notes"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setMode('search')} className="flex-1" data-testid="unmatched-back-btn">
                Back to search
              </Button>
              <Button
                onClick={handleCreateUnmatched}
                disabled={!unmatchedForm.product_name.trim() || submitting}
                className="flex-1"
                data-testid="unmatched-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Log Item
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
